import React from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PreloadableLazy<T extends React.ComponentType<any>> = React.LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>;
};

const isChunkFailure = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const message =
    "message" in error && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  const name =
    "name" in error && typeof (error as { name?: unknown }).name === "string"
      ? (error as { name: string }).name
      : "";
  const haystack = `${name} ${message}`.toLowerCase();
  return (
    haystack.includes("chunkloaderror") ||
    haystack.includes("loading chunk") ||
    haystack.includes("failed to fetch dynamically imported module") ||
    haystack.includes("error loading dynamically imported module") ||
    haystack.includes("importing a module script failed")
  );
};

/**
 * Lazy load with a single automatic retry for transient chunk failures,
 * then rethrow so ErrorHandler can show the stale-client recovery UI.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithPreload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreloadableLazy<T> {
  const load = (): Promise<{ default: T }> =>
    factory().catch(async (error: unknown) => {
      if (!isChunkFailure(error)) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
      return factory();
    });

  const Component = React.lazy(load) as PreloadableLazy<T>;
  Component.preload = load;
  return Component;
}
